import { useState, useRef, useEffect, useMemo } from "react";
import { ICON_NAMES } from "@adlywise/shared";
import * as LucideIcons from "lucide-react";

const MAX_VISIBLE = 60;

export default function IconPicker({
  value,
  onChange,
  label = "Icon",
  placeholder = "Search lucide icons…",
  iconOnly = false,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const containerRef = useRef(null);
  const searchRef = useRef(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return ICON_NAMES.slice(0, MAX_VISIBLE);
    return ICON_NAMES.filter((n) => n.toLowerCase().includes(q));
  }, [query]);

  const visible = filtered.slice(0, MAX_VISIBLE);
  const overflow = filtered.length - visible.length;

  useEffect(() => {
    const handleMouseDown = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, []);

  useEffect(() => {
    if (open && searchRef.current) searchRef.current.focus();
  }, [open]);

  const toggleOpen = () => {
    setOpen((prev) => !prev);
    setQuery("");
  };

  const handleSelect = (name) => {
    onChange(name);
    setOpen(false);
    setQuery("");
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange("");
    setOpen(false);
    setQuery("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      setOpen(false);
      setQuery("");
    } else if (e.key === "Enter" && open && visible.length > 0) {
      e.preventDefault();
      handleSelect(visible[0]);
    }
  };

  const SelectedIcon = value ? LucideIcons[value] : null;

  return (
    <div className="relative" ref={containerRef}>
      {label && !iconOnly && (
        <label className="mb-1 block text-sm font-medium text-(--text-primary)">
          {label}
        </label>
      )}

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={toggleOpen}
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-label="Choose icon"
          className={[
            "flex h-11 w-11 shrink-0 items-center justify-center rounded-(--radius-md) border bg-(--surface-1) transition-colors",
            open
              ? "border-(--brand-primary) text-(--brand-primary)"
              : "border-(--border-strong) text-(--text-secondary) hover:border-(--brand-primary)",
          ].join(" ")}
        >
          {SelectedIcon ? (
            <SelectedIcon size={20} strokeWidth={2} />
          ) : (
            <LucideIcons.Image
              size={18}
              strokeWidth={1.8}
              className="text-(--text-faint)"
            />
          )}
        </button>

        {!iconOnly && (
          <button
            type="button"
            onClick={toggleOpen}
            className={[
              "flex flex-1 items-center justify-between rounded-(--radius-md) border bg-(--surface-1) px-3 py-2 text-left text-sm transition-colors",
              open
                ? "border-(--brand-primary)"
                : "border-(--border-subtle) hover:border-(--brand-primary)",
            ].join(" ")}
          >
            <span
              className={
                value ? "text-(--text-primary)" : "text-(--text-muted)"
              }
            >
              {value || "Choose an icon"}
            </span>
            {value ? (
              <span
                role="button"
                tabIndex={-1}
                onClick={handleClear}
                className="flex items-center text-(--text-secondary) hover:text-(--text-primary)"
                aria-label="Clear icon"
              >
                <LucideIcons.X size={15} strokeWidth={2} />
              </span>
            ) : (
              <LucideIcons.ChevronDown
                size={15}
                strokeWidth={2}
                className="text-(--text-muted)"
              />
            )}
          </button>
        )}
      </div>

      {open && (
        <div
          role="dialog"
          className={[
            "absolute left-0 z-20 mt-2 rounded-(--radius-md) border border-(--border-subtle) bg-(--surface-1) p-3 shadow-[var(--shadow-popover)]",
            iconOnly ? "w-72" : "right-0",
          ].join(" ")}
        >
          <div className="relative">
            <LucideIcons.Search
              size={15}
              strokeWidth={2}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-(--text-muted)"
            />
            <input
              ref={searchRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              autoComplete="off"
              className="w-full rounded-(--radius-md) border border-(--border-subtle) bg-(--surface-1) py-2 pl-9! pr-3 text-sm text-(--text-primary) outline-none focus:border-(--brand-primary)"
            />
          </div>

          {visible.length === 0 ? (
            <div className="px-1 py-6 text-center text-sm text-(--text-secondary)">
              No icons found
            </div>
          ) : (
            <>
              <div className="mt-3 grid max-h-60 grid-cols-7 gap-1 overflow-y-auto sm:grid-cols-8">
                {visible.map((name) => {
                  const Icon = LucideIcons[name];
                  const isActive = name === value;
                  return (
                    <button
                      key={name}
                      type="button"
                      title={name}
                      aria-label={name}
                      aria-pressed={isActive}
                      onClick={() => handleSelect(name)}
                      className={[
                        "flex aspect-square cursor-pointer items-center justify-center rounded-(--radius-sm) border-0 transition-colors",
                        isActive
                          ? "bg-(--pastel-violet) text-(--brand-primary)"
                          : "bg-transparent text-(--text-secondary) hover:bg-(--surface-2) hover:text-(--text-primary)",
                      ].join(" ")}
                    >
                      {Icon ? (
                        <Icon size={18} strokeWidth={2} />
                      ) : (
                        <span className="h-4 w-4" />
                      )}
                    </button>
                  );
                })}
              </div>
              {overflow > 0 && (
                <p className="mt-2 px-1 text-xs text-(--text-muted)">
                  Showing {visible.length} of {filtered.length} — keep typing to
                  narrow down.
                </p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
